import { createFileRoute } from "@tanstack/react-router";
import MealTokenLanding from "@/components/MealTokenLanding";
import heroCoinAsset from "@/assets/meal4-token.png.asset.json";

import {
  HREFLANG,
  SUPPORTED_LOCALES,
  absUrl,
  faqPageJsonLd,
  isSeoLang,
  organizationJsonLd,
  type SeoLang,
} from "@/lib/seo-jsonld";

const TITLE = "Meal4 & Meal2 — Tokenized Food Security on Solana";
const DESCRIPTION =
  "SFN (Sustainable Food & Eco Network) is a dual-token food security protocol on Solana. MEAL4 is the deflationary inflation shield (1,000,000 hard cap). MEAL2 powers daily meals at our anchor kitchen in Thailand Sandbox.";
const OG_IMAGE = absUrl("/og-image.jpg");
const OG_LOCALE: Record<SeoLang, string> = {
  en: "en_US",
  de: "de_DE",
  zh: "zh_CN",
  ja: "ja_JP",
  ko: "ko_KR",
  ru: "ru_RU",
  pt: "pt_BR",
};

export const Route = createFileRoute("/")({
  component: MealTokenLanding,
  validateSearch: (search: Record<string, unknown>): { lang?: SeoLang } =>
    isSeoLang(search.lang) ? { lang: search.lang } : {},
  head: ({ match }) => {
    const lang: SeoLang = isSeoLang(match.search?.lang) ? match.search.lang : "en";
    const selfUrl = absUrl(lang === "en" ? "/" : `/?lang=${lang}`);
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        {
          name: "keywords",
          content:
            "Meal4, Meal2, SFN, Sustainable Food & Eco Network, food security token, Solana, dual-token, inflation hedge, Thailand Sandbox, SolarPando",
        },
        { name: "author", content: "Sustainable Food & Eco Network" },
        { name: "theme-color", content: "#0a0a0a" },

        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Sustainable Food & Eco Network" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: selfUrl },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "SFN • Sustainable Food & Eco Network — Freezing the Price of Food" },
        { property: "og:locale", content: OG_LOCALE[lang] },
        ...SUPPORTED_LOCALES.filter((l) => l !== lang).map((l) => ({
          property: "og:locale:alternate",
          content: OG_LOCALE[l],
        })),

        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@Neutral_Frame" },
        { name: "twitter:creator", content: "@Neutral_Frame" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: OG_IMAGE },
        { name: "twitter:image:alt", content: "SFN • Sustainable Food & Eco Network — Freezing the Price of Food" },
      ],
      links: [
        { rel: "canonical", href: selfUrl },
        {
          rel: "preload",
          as: "image",
          href: heroCoinAsset.url,
          fetchPriority: "high",
        },

        ...SUPPORTED_LOCALES.map((l) => ({
          rel: "alternate",
          hrefLang: HREFLANG[l],
          href: absUrl(l === "en" ? "/" : `/?lang=${l}`),
        })),
        { rel: "alternate", hrefLang: "x-default", href: absUrl("/") },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(organizationJsonLd(lang, DESCRIPTION)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqPageJsonLd(lang)),
        },
      ],
    };
  },
});
